import { PureComponent, Fragment } from 'react';
import { Radio } from 'antd';
import type { RadioChangeEvent } from 'antd';

import {
    fetchMatchingAllergies,
    fetchMatchingDiagnose,
    fetchMatchingDrugs,
    fetchMatchingProcedures,
} from '../../api';
import { MedicalHealthConfigAutoSuggest } from './MedicalHistoryConfigSearchInput';
import {
    currentMedicationsIcon,
    drugAllergyIcon,
    familyHistoryIcon,
    foodOtherAllergyIcon,
    lifestyleHabitsIcon,
    otherMedicalHistoryIcon,
    pastProceduresIcon,
    patientMedicalConditionsIcon,
    recentTravelHistoryIcon,
} from '../common/Icons';
import { MEDICAL_HISTORY_CATEGORY, MHItem } from '../../store/types/medicalHistory';

export const SELECTED_PATIENT_HISTORY_ITEMS = 'SELECTED_PATIENT_HISTORY_ITEMS';

type State = {
    selectedSection: string;
};

const frequentlyUsedLifeStyleOptions = [
    { id: '1', name: 'Tobacco' },
    { id: '2', name: 'Smoke' },
    { id: '3', name: 'Alcohol' },
    { id: '4', name: 'Others' },
];

interface Props {
    setMedicalHistoryCommonItemsSectionData: (id: MEDICAL_HISTORY_CATEGORY, option: MHItem) => void;
    medicalHistoryCommonItems: ReadonlyArray<MHItem>;
    deleteMedicalHistoryCommonItemsElement: (id: string) => void;
    disabled?: boolean;
}

const MEDICAL_HISTORY_CONFIG = 'MEDICAL_HISTORY_CONFIG';

export const medicalHistoryOptions = {
    patientMedicalConditions: {
        name: 'Existing Conditions',
        func: fetchMatchingDiagnose,
        display: 'since',
        searchIcon: patientMedicalConditionsIcon,
        inputContainerOptions: undefined,
        initialRef: 1,
    },
    lifestyleHabits: {
        name: 'Lifestyle Habits',
        func: undefined,
        display: 'frequency',
        searchIcon: lifestyleHabitsIcon,
        inputContainerOptions: undefined,
        initialRef: 1,
    },
    drugAllergy: {
        name: 'Drug Allergy',
        func: fetchMatchingDrugs,
        display: 'since',
        searchIcon: drugAllergyIcon,
        inputContainerOptions: undefined,
        initialRef: 1,
    },
    foodOtherAllergy: {
        name: 'Other Allergy',
        func: fetchMatchingAllergies,
        display: 'since',
        searchIcon: foodOtherAllergyIcon,
        inputContainerOptions: undefined,
        initialRef: 1,
    },

    familyHistory: {
        name: 'Family History',
        func: fetchMatchingDiagnose,
        display: undefined,
        searchIcon: familyHistoryIcon,
        inputContainerOptions: undefined,
        initialRef: 4,
    },
    currentMedications: {
        name: 'Current Medications',
        func: fetchMatchingDrugs,
        display: undefined,
        searchIcon: currentMedicationsIcon,
        inputContainerOptions: undefined,
        initialRef: 1,
    },
    pastProcedures: {
        name: 'Past Procedures',
        func: fetchMatchingProcedures,
        display: undefined,
        searchIcon: pastProceduresIcon,
        inputContainerOptions: undefined,
        initialRef: 6,
    },
    recentTravelHistory: {
        name: 'Travel History',
        func: undefined,
        display: undefined,
        searchIcon: recentTravelHistoryIcon,
        inputContainerOptions: {
            text: 'Enter notes here',
            heading: 'Enter notes:',
        },
        initialRef: undefined,
    },
    otherMedicalHistory: {
        name: 'Other Medical History',
        func: undefined,
        display: undefined,
        searchIcon: otherMedicalHistoryIcon,
        inputContainerOptions: {
            text: 'Enter title here',
            heading: 'Enter Title:',
        },
        initialRef: 3,
    },
};

class CustomiseSection extends PureComponent<Props, State> {
    state: State = {
        selectedSection: MEDICAL_HISTORY_CATEGORY.PATIENT_MEDICAL_CONDITIONS,
    };

    onChange = (e: RadioChangeEvent) => {
        this.setState({ selectedSection: e.target.value });
    };

    render() {
        const { medicalHistoryCommonItems, disabled } = this.props;
        const { selectedSection } = this.state;
        const recentOptions = frequentlyUsedLifeStyleOptions.filter((ele) => {
            const data = medicalHistoryCommonItems.find((mhEle) => mhEle.id === ele.id);
            return data ? false : true;
        });
        return (
            <Fragment>
                <div className="p-16 flex flex-col lg:max-h-400 max-h-462 space-y-16">
                    <div className="flex h-full rounded-8 bg-white lg:flex-row flex-col">
                        <div
                            className={`flex p-16 lg:mb-0 mb-16 flex-col flex-1 ${
                                disabled ? 'pointer-events-none' : ''
                            }`}
                        >
                            <p className="text-12 text-darwin-neutral-1000 whitespace-nowrap">
                                CUSTOMISE QUICK LINKS
                            </p>
                            <Radio.Group
                                onChange={this.onChange}
                                value={selectedSection}
                                className="flex justify-between p-16 whitespace-nowrap"
                                disabled={disabled ?? false}
                            >
                                <Radio value={MEDICAL_HISTORY_CATEGORY.PATIENT_MEDICAL_CONDITIONS}>
                                    Condition
                                </Radio>
                                <Radio value={MEDICAL_HISTORY_CATEGORY.DRUG_ALLERGY}>
                                    Drug Allergy
                                </Radio>
                                <Radio value={MEDICAL_HISTORY_CATEGORY.FOOD_OTHER_ALLERGY}>
                                    Other Allergy
                                </Radio>
                                <Radio value={MEDICAL_HISTORY_CATEGORY.LIFESTYLE_HABITS}>
                                    Lifestyle Habits
                                </Radio>
                            </Radio.Group>
                            <MedicalHealthConfigAutoSuggest
                                key={selectedSection}
                                setMedicalHistoryCommonItemsSectionData={
                                    this.props.setMedicalHistoryCommonItemsSectionData
                                }
                                medicalHistoryCommonItems={this.props.medicalHistoryCommonItems}
                                selectedSection={selectedSection as MEDICAL_HISTORY_CATEGORY}
                                elidPrefixId={MEDICAL_HISTORY_CONFIG}
                                api={
                                    medicalHistoryOptions[
                                        this.state
                                            .selectedSection as keyof typeof medicalHistoryOptions
                                    ].func
                                }
                            />
                            {selectedSection === MEDICAL_HISTORY_CATEGORY.LIFESTYLE_HABITS && (
                                <div className="flex flex-wrap">
                                    {recentOptions?.map((r) => (
                                        <span
                                            key={r.id}
                                            onClick={() =>
                                                this.props.setMedicalHistoryCommonItemsSectionData(
                                                    this.state
                                                        .selectedSection as MEDICAL_HISTORY_CATEGORY,
                                                    r,
                                                )
                                            }
                                            className="inline-flex items-center px-12 py-4 mt-8 mr-8 border cursor-pointer text-docM-blue-400 border-docM-blue-100 rounded-24 text-14"
                                        >
                                            {r.name}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex relative pt-0 flex-col border-t-1 border-darwin-neutral-100 lg:w-40p lg:border-darwin-neutral-100 lg:border-l-1  overflow-auto w-full">
                            <p
                                className="text-12 text-darwin-neutral-1000 whitespace-nowrap py-12 bg-white absolute top-0 w-full text-center"
                                style={{ boxShadow: '0 1px 4px 0 rgb(0 0 0 / 40%)' }}
                            >
                                SELECTED QUICK LINKS
                            </p>
                            <div
                                className="flex flex-wrap items-center overflow-auto mt-48 mx-20 pb-20"
                                id={SELECTED_PATIENT_HISTORY_ITEMS}
                            >
                                {this.props.medicalHistoryCommonItems.map((d, index) => (
                                    <span
                                        id={d.id}
                                        key={d.id}
                                        role="button"
                                        className={`flex items-center mt-8 mr-8 ${
                                            disabled
                                                ? 'border pointer-events-none text-13 rounded-16 py-4 px-12 border-darwin-neutral_new-200 text-darwin-neutral_new-200'
                                                : 'tab-chip'
                                        }`}
                                    >
                                        {d.name}
                                        <div
                                            className="flex justify-end items-center w-12 h-20 w-20"
                                            onClick={() => {
                                                if (!disabled) {
                                                    this.props.deleteMedicalHistoryCommonItemsElement(
                                                        d.id,
                                                    );
                                                }
                                            }}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="9"
                                                height="11"
                                                viewBox="0 0 9 11"
                                                fill="none"
                                                className="ml-8"
                                            >
                                                <path
                                                    d="M1.03516 9.37109C1.05469 9.85938 1.48438 10.25 1.97266 10.25H6.75781C7.24609 10.25 7.67578 9.85938 7.69531 9.37109L8.125 2.75H0.625L1.03516 9.37109ZM8.4375 0.875H6.25L6.01562 0.425781C5.95703 0.328125 5.85938 0.25 5.74219 0.25H2.98828C2.87109 0.25 2.77344 0.328125 2.71484 0.425781L2.5 0.875H0.3125C0.136719 0.875 0 1.03125 0 1.1875V1.8125C0 1.98828 0.136719 2.125 0.3125 2.125H8.4375C8.59375 2.125 8.75 1.98828 8.75 1.8125V1.1875C8.75 1.03125 8.59375 0.875 8.4375 0.875Z"
                                                    fill={`${disabled ? '#D1D1D1' : '#B04E43'}`}
                                                />
                                            </svg>
                                        </div>
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }
}

export default CustomiseSection;
